export default () => ({
    isOpen: false,
    detail: {},
    closeTimer: null,
    closeCleanup: null,
    closeInstantHandler: null,
    focusTrapHandler: null,
    hashChangeHandler: null,
    wasLenisStopped: false,

    init() {
        this.closeInstantHandler = (event) => {
            this.closeInstant({
                syncHash: event.detail?.syncHash ?? false,
            });
        };
        this.focusTrapHandler = (event) => this.trapTabFocus(event);
        this.hashChangeHandler = () => this.syncWithHash();

        if (!this.$root.hasAttribute('tabindex')) {
            this.$root.setAttribute('tabindex', '-1');
        }

        this.$root.addEventListener('dialog-close-instant', this.closeInstantHandler);
        this.$root.addEventListener('keydown', this.focusTrapHandler);
        window.addEventListener('hashchange', this.hashChangeHandler);

        requestAnimationFrame(() => {
            this.syncWithHash();
        });
    },

    destroy() {
        if (this.closeInstantHandler) {
            this.$root.removeEventListener('dialog-close-instant', this.closeInstantHandler);
            this.closeInstantHandler = null;
        }

        if (this.focusTrapHandler) {
            this.$root.removeEventListener('keydown', this.focusTrapHandler);
            this.focusTrapHandler = null;
        }

        if (this.hashChangeHandler) {
            window.removeEventListener('hashchange', this.hashChangeHandler);
            this.hashChangeHandler = null;
        }

        window.clearTimeout(this.closeTimer);
        this.closeCleanup?.();
    },

    open(options = {}) {
        const { syncHash = true, detail = null } = options;

        this.closeOtherDialogs();
        this.closeCleanup?.();
        this.detail = detail ?? this.detail;

        if (!this.$root.open) {
            this.$root.showModal();
            this.resetScrollPosition();
            this.focusDialog();
        }

        this.isOpen = true;
        this.lockScroll();
        this.$root.classList.remove('dialog--closing');

        requestAnimationFrame(() => {
            this.$root.classList.add('dialog--open');
        });

        this.playMedia();

        if (syncHash && this.shouldSyncHash()) {
            this.updateHash();
        }
    },

    close(options = {}) {
        const { syncHash = true } = options;

        if (!this.$root.open || this.$root.classList.contains('dialog--closing')) {
            return;
        }

        this.pauseMedia();

        const dialog = this.$root;
        const scroll = dialog.querySelector('.dialog__scroll');
        let isFinished = false;

        function onTransitionEnd(event) {
            if (event.target === scroll && event.propertyName === 'transform') {
                finishClose();
            }
        }

        const cleanup = () => {
            scroll?.removeEventListener('transitionend', onTransitionEnd);
            window.clearTimeout(this.closeTimer);
            this.closeTimer = null;
            this.closeCleanup = null;
        };
        const finishClose = () => {
            if (isFinished) {
                return;
            }

            isFinished = true;
            cleanup();
            dialog.close();
            this.isOpen = false;
            dialog.classList.remove('dialog--closing');
            this.unlockScroll();

            if (syncHash && this.shouldSyncHash()) {
                this.clearHash();
            }
        };

        dialog.classList.remove('dialog--open');
        dialog.classList.add('dialog--closing');
        scroll?.addEventListener('transitionend', onTransitionEnd);
        this.closeCleanup = () => {
            if (isFinished) {
                return;
            }

            isFinished = true;
            cleanup();
            dialog.classList.remove('dialog--closing');
        };
        this.closeTimer = window.setTimeout(finishClose, 600);
    },

    closeInstant(options = {}) {
        const { syncHash = true } = options;

        this.closeCleanup?.();
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
        this.closeCleanup = null;

        const wasOpen = this.$root.open;

        this.pauseMedia();

        if (wasOpen) {
            this.$root.close();
        }

        this.isOpen = false;
        this.$root.classList.remove('dialog--open', 'dialog--closing');

        if (wasOpen) {
            this.unlockScroll();
        }

        if (syncHash && this.shouldSyncHash()) {
            this.clearHash();
        }
    },

    syncWithHash() {
        if (!this.shouldSyncHash()) {
            return;
        }

        const hashId = this.getHashId();

        if (!this.$root.id) {
            return;
        }

        if (hashId === this.$root.id) {
            if (!this.$root.open) {
                this.open({ syncHash: false });
            }

            return;
        }

        if (this.$root.open) {
            this.closeInstant({ syncHash: false });
        }
    },

    closeOtherDialogs() {
        document.querySelectorAll('.dialog[open]').forEach((dialog) => {
            if (dialog === this.$root) {
                return;
            }

            dialog.dispatchEvent(new CustomEvent('dialog-close-instant', {
                detail: {
                    syncHash: false,
                },
            }));
        });
    },

    trapTabFocus(event) {
        if (!this.$root.open || event.key !== 'Tab') {
            return;
        }

        const focusableElements = this.getFocusableElements();

        if (!focusableElements.length) {
            event.preventDefault();
            this.focusDialog();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;

        if (!this.$root.contains(activeElement) || activeElement === this.$root) {
            event.preventDefault();
            this.focusElement(event.shiftKey ? lastElement : firstElement);
            return;
        }

        if (event.shiftKey && activeElement === firstElement) {
            event.preventDefault();
            this.focusElement(lastElement);
            return;
        }

        if (!event.shiftKey && activeElement === lastElement) {
            event.preventDefault();
            this.focusElement(firstElement);
        }
    },

    getFocusableElements() {
        const selector = [
            'a[href]',
            'area[href]',
            'button:not([disabled])',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'iframe',
            'object',
            'embed',
            'audio[controls]',
            'video[controls]',
            '[contenteditable]:not([contenteditable="false"])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(',');

        return Array.from(this.$root.querySelectorAll(selector)).filter((element) => {
            const tabIndex = element.getAttribute('tabindex');

            if (tabIndex !== null && Number(tabIndex) < 0) {
                return false;
            }

            if (element.matches('[disabled]') || element.closest('[hidden], [aria-hidden="true"]')) {
                return false;
            }

            const style = window.getComputedStyle(element);

            return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
        });
    },

    focusDialog() {
        this.focusElement(this.$root);
    },

    focusElement(element) {
        try {
            element.focus({ preventScroll: true });
        } catch (error) {
            element.focus();
        }
    },

    resetScrollPosition() {
        const scroll = this.$root.querySelector('.dialog__scroll');

        this.$root.scrollTop = 0;

        if (scroll) {
            scroll.scrollTop = 0;
            scroll.scrollLeft = 0;
        }
    },

    pauseMedia() {
        this.$root.querySelectorAll('audio, video').forEach((media) => {
            media.pause?.();
        });
    },

    shouldSyncHash() {
        return this.$root.dataset.dialogHash !== 'false';
    },

    playMedia() {
        this.$nextTick(() => {
            this.$root.querySelectorAll('[data-dialog-media]').forEach((media) => {
                media.load?.();
                media.play?.()?.catch?.(() => {});
            });
        });
    },

    updateHash() {
        if (!this.$root.id) {
            return;
        }

        const nextHash = `#${encodeURIComponent(this.$root.id)}`;

        if (window.location.hash === nextHash) {
            return;
        }

        window.history.pushState(null, '', nextHash);
    },

    clearHash() {
        if (!this.$root.id || this.getHashId() !== this.$root.id) {
            return;
        }

        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    },

    getHashId() {
        const hash = window.location.hash.slice(1);

        if (!hash) {
            return '';
        }

        try {
            return decodeURIComponent(hash);
        } catch (error) {
            return hash;
        }
    },

    lockScroll() {
        if (window.pageScrollLock) {
            window.pageScrollLock.lock('dialog');
            return;
        }

        const lenis = window.lenis;

        this.wasLenisStopped = Boolean(lenis?.isStopped);
        document.documentElement.classList.add('dialog-noscroll');
        lenis?.stop?.();
    },

    unlockScroll() {
        if (window.pageScrollLock) {
            window.pageScrollLock.unlock('dialog');
            return;
        }

        document.documentElement.classList.remove('dialog-noscroll');

        if (!this.wasLenisStopped) {
            window.lenis?.start?.();
        }
    },
});
